import { Box, Typography, Button, Avatar, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  useFollowUserMutation,
  useUnfollowUserMutation,
  useGetUserByUidQuery,
} from "@/store/api/userApi";
import { useState, useEffect } from "react";
import { useAppSelector } from "@/hooks/useAppSelector";
import { styles } from "./stylesUserInfoPopup";
import HeartSvg from "@/assets/svg/HeartSvg";
import LightningSvg from "@/assets/svg/LightningSvg";
import TopEarnerBadge from "@/components/UI/TopEarnerBadge/TopEarnerBadge";
import { formatMoney } from "@/helpers/formatMoney";

interface UserInfoPopupProps {
  uid: string;
}

const formatEarned = (value: number): string => {
  if (value >= 1000) {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return formatMoney(value);
};

export default function UserInfoPopup({ uid }: UserInfoPopupProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetUserByUidQuery(uid, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();
  const { user: authUser } = useAppSelector((state) => state.auth);
  const myProfile = uid === authUser?.uid;

  const [isFollowingState, setIsFollowingState] = useState(false);
  const isFollowStatusLoaded = data?.isSubscribed !== undefined;

  useEffect(() => {
    if (isFollowStatusLoaded) {
      setIsFollowingState(data.isSubscribed);
    }
  }, [data?.isSubscribed]);

  const handleFollowClick = async () => {
    try {
      if (isFollowingState) {
        await unfollowUser(uid).unwrap();
      } else {
        await followUser(uid).unwrap();
      }
      setIsFollowingState(!isFollowingState);
    } catch (error) {
      console.error("Follow/Unfollow error:", error);
    }
  };

  const formattedBio = data?.bio?.split(" ").map((word, index) =>
    word.startsWith("$") ? (
      <span key={index} style={{ color: "#1976d2" }}>
        {word}{" "}
      </span>
    ) : (
      word + " "
    )
  );

  if (isLoading || !data) {
    return (
      <Box sx={styles.wrapper}>
        <Box sx={styles.topWrapper}>
          <Box sx={styles.headerCol}>
            <Box sx={styles.headerRow}>
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton variant="rounded" width={91} height={32} sx={{ borderRadius: "26px" }} />
            </Box>
            <Box sx={styles.infoCol}>
              <Skeleton variant="text" width={120} sx={{ fontSize: "15px" }} />
              <Box sx={styles.metaCol}>
                <Skeleton variant="text" width={150} sx={{ fontSize: "17px" }} />
                <Skeleton variant="text" width={170} sx={{ fontSize: "12px" }} />
              </Box>
            </Box>
          </Box>
          <Box sx={styles.bioBox}>
            <Skeleton variant="text" width="100%" sx={{ fontSize: "12px" }} />
            <Skeleton variant="text" width="100%" sx={{ fontSize: "12px" }} />
            <Skeleton variant="text" width="100%" sx={{ fontSize: "12px" }} />
            <Skeleton variant="text" width="60%" sx={{ fontSize: "12px" }} />
          </Box>
        </Box>
        <Box sx={styles.footer}>
          <Skeleton variant="text" width={80} sx={{ fontSize: "13px" }} />
          <Skeleton variant="text" width={80} sx={{ fontSize: "13px" }} />
        </Box>
      </Box>
    );
  }

  if (isError) return null;

  return (
    <Box sx={styles.wrapper} onClick={() => navigate(`/profile/${uid}`)}>
      <Box sx={styles.topWrapper}>
        <Box sx={styles.headerCol}>
          <Box sx={styles.headerRow}>
            <Avatar src={data.image?.url} alt={data.username} sx={styles.avatar} />
            {!myProfile && isFollowStatusLoaded && (
              <Button
                variant="contained"
                sx={styles.followButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFollowClick();
                }}
              >
                {isFollowingState ? "Unfollow" : "Follow"}
              </Button>
            )}
          </Box>

          <Box sx={styles.infoCol}>
            <Box sx={styles.usernameRow}>
              <Typography sx={styles.username}>@{data.username}</Typography>
              {!!data.topEarnerRank && <TopEarnerBadge rank={data.topEarnerRank} />}
            </Box>

            <Box sx={styles.metaCol}>
              <Box sx={styles.earningsRow}>
                <Typography component="span" sx={styles.earningsValue}>
                  ${formatEarned(data.totalEarned ?? 0)}
                </Typography>
                <Typography component="span" sx={styles.earningsLabel}>
                  total earned
                </Typography>
              </Box>

              <Box sx={styles.statsRow}>
                <Box sx={styles.statChip}>
                  <HeartSvg width={18} height={18} />
                  <Typography component="span" sx={styles.statChipText}>
                    {data.likesCount.toLocaleString()} likes
                  </Typography>
                </Box>
                <Box sx={styles.statChip}>
                  <LightningSvg width={18} height={18} />
                  <Typography component="span" sx={styles.statChipText}>
                    {data.zapCount.toLocaleString()} zaps
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.bioBox}>
          <Typography sx={styles.bio}>{formattedBio}</Typography>
        </Box>
      </Box>

      <Box sx={styles.footer}>
        <Box sx={styles.statItem}>
          <Typography component="span" sx={styles.statValue}>
            {data.followingCount.toLocaleString()}
          </Typography>
          <Typography component="span" sx={styles.statLabel}>
            Following
          </Typography>
        </Box>
        <Box sx={styles.statItem}>
          <Typography component="span" sx={styles.statValue}>
            {data.followersCount.toLocaleString()}
          </Typography>
          <Typography component="span" sx={styles.statLabel}>
            Followers
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
